// =============================================================================
// Shared field helpers for config-driven pickers (AutocompleteField /
// SearchableSelect / refDataCache). Pure functions, no React — safe to import
// from both the component and util layers.
//
// NOTE: AutocompleteField.js keeps its own private copies of these (it predates
// this module and is heavily used); we deliberately leave it untouched to avoid
// regressing existing callers. New code should import from here.
// =============================================================================

// Resolves a data path like "territories[0].salesTRPs" against a JSON object.
export function resolveDataPath(obj, path) {
  if (!obj || !path) return undefined;
  const tokens = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current = obj;
  for (const token of tokens) {
    if (current == null) return undefined;
    if (/^\d+$/.test(token)) {
      current = Array.isArray(current) ? current[parseInt(token, 10)] : undefined;
    } else {
      current = current[token];
    }
  }
  return current;
}

// Look up a property on `obj` case-insensitively. Existing DB configs use
// PascalCase (e.g. "TerritoryID") while .NET serialises camelCase
// ("territoryID") — supporting both keeps old and new configs working.
export function pickField(obj, key) {
  if (!obj || !key) return undefined;
  if (obj[key] !== undefined) return obj[key];
  const target = key.toLowerCase();
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase() === target) return obj[k];
  }
  return undefined;
}

// secondaryField may be a string or an array. Returns a comma-joined display
// string, or "" if nothing resolves.
export function extractSecondary(item, secondaryField) {
  if (!item || !secondaryField) return "";
  if (Array.isArray(secondaryField)) {
    return secondaryField
      .map((k) => pickField(item, k))
      .filter((v) => v !== undefined && v !== null && v !== "")
      .join(", ");
  }
  const v = pickField(item, secondaryField);
  return v == null ? "" : String(v);
}

/**
 * Strip a leading repetition of the code from its own description.
 *
 * The master-data feeds return the code inside the description as well —
 * "WD40-R19" comes back with "WD40-R19 Kiran Kumar", "WD40" with "WD40 Shukla
 * IT". Rendered side by side that prints the code twice on one row and steals
 * the width the actual name needs. Returns the description unchanged when it
 * does not begin with the code, so a genuine name starting with similar
 * characters is never truncated.
 */
export function dedupeSecondary(display, secondary) {
  const d = String(display ?? "").trim();
  const s = String(secondary ?? "").trim();
  if (!d || !s) return s;
  if (s.toUpperCase() === d.toUpperCase()) return "";          // pure repeat
  if (s.toUpperCase().startsWith(d.toUpperCase())) {
    // Only strip when a separator follows, so "WD40" does not mangle "WD400 Ltd".
    const rest = s.slice(d.length);
    if (/^[\s\-–—:·,|]/.test(rest)) return rest.replace(/^[\s\-–—:·,|]+/, "").trim() || s;
  }
  return s;
}
